import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useRef, useState } from 'react';
import { Button, FormControl, FormHelperText, MenuItem, Select, TextField, Typography, IconButton } from '@mui/material';
import { Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { MapUnitNamesSection } from '../MapUnitNamesSection';
const CELL_BORDER = '1px solid #e0e0e0';
export const COLUMN_FIELD_MAP = [{
  label: 'Start read date',
  field: 'startReadDateColumn'
}, {
  label: 'End read date',
  field: 'endReadDateColumn'
}, {
  label: 'Start read',
  field: 'startReadColumn'
}, {
  label: 'End read',
  field: 'endReadColumn'
}, {
  label: 'Usage',
  field: 'usageColumn'
}, {
  label: 'Meter ID',
  field: 'meterIdColumn'
}, {
  label: 'Unit name',
  field: 'unitNameColumn'
}, {
  label: 'Unit of measure',
  field: 'uomColumn'
}, {
  label: 'Provider phone',
  field: 'providerPhoneColumn',
  optional: true
}, {
  label: 'Billing agent disclosure',
  field: 'billingAgentDisclosureColumn',
  optional: true
}, {
  label: 'Leak service contact',
  field: 'leakServiceContactColumn',
  optional: true
}];
const normalizeColumnName = value => (value || '').trim().toLowerCase();
const findDuplicateColumns = columnMappings => COLUMN_FIELD_MAP.reduce((acc, _ref) => {
  let field = _ref.field;
  const name = normalizeColumnName(columnMappings[field]);
  if (!name) return acc;
  const other = COLUMN_FIELD_MAP.find(f => f.field !== field && normalizeColumnName(columnMappings[f.field]) === name);
  return other ? _objectSpread(_objectSpread({}, acc), {}, {
    [field]: "Already used for ".concat(other.label)
  }) : acc;
}, {});

// Module-level observer — re-renders only when validity/inProgress changes
const CreateSaveButton = observer(_ref2 => {
  let validityStore = _ref2.validityStore,
    onClick = _ref2.onClick;
  const store = useStore();
  return /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: onClick,
    disabled: store.allocations.postSubmeterBillingCsvMapAsync.inProgress || !validityStore.isFormValid || !validityStore.isUnitValid
  }, "Save mapping");
});
const EditSaveButton = observer(_ref3 => {
  let validityStore = _ref3.validityStore,
    onClick = _ref3.onClick;
  const store = useStore();
  return /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    onClick: onClick,
    disabled: store.allocations.putSubmeterBillingCsvMapAsync.inProgress || !validityStore.isFormValid || !validityStore.isUnitValid
  }, "Save mapping");
});

// Defined at module level — never remounts due to parent re-renders
const MapColumnsFormContent = _ref4 => {
  let dataRef = _ref4.dataRef,
    onValidChange = _ref4.onValidChange,
    initialValues = _ref4.initialValues;
  const _useState = useState((initialValues === null || initialValues === void 0 ? void 0 : initialValues.name) || ''),
    _useState2 = _slicedToArray(_useState, 2),
    mappingName = _useState2[0],
    setMappingName = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    csvFile = _useState4[0],
    setCsvFile = _useState4[1];
  const _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    csvHeaders = _useState6[0],
    setCsvHeaders = _useState6[1];
  const _useState7 = useState(() => COLUMN_FIELD_MAP.reduce((acc, _ref5) => {
      let field = _ref5.field;
      return _objectSpread(_objectSpread({}, acc), {}, {
        [field]: (initialValues === null || initialValues === void 0 ? void 0 : initialValues[field]) || ''
      });
    }, {})),
    _useState8 = _slicedToArray(_useState7, 2),
    columnMappings = _useState8[0],
    setColumnMappings = _useState8[1];
  const _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    isDragging = _useState0[0],
    setIsDragging = _useState0[1];
  const _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    showErrors = _useState10[0],
    setShowErrors = _useState10[1];
  const fileInputRef = useRef(null);
  const isNameValid = !!mappingName.trim();
  const duplicateColumns = findDuplicateColumns(columnMappings);
  const isColumnsValid = COLUMN_FIELD_MAP.filter(f => !f.optional).every(_ref6 => {
    let field = _ref6.field;
    return !!columnMappings[field];
  }) && !Object.keys(duplicateColumns).length;
  const isValid = isNameValid && isColumnsValid;
  onValidChange === null || onValidChange === void 0 || onValidChange(isValid);
  const parseHeaders = async file => {
    try {
      const text = await file.text();
      const firstLine = text.split('\n')[0];
      const headers = firstLine.split(',').map(h => h.trim().replace(/^"|"$/g, '').trim()).filter(Boolean);
      setCsvHeaders(headers);
    } catch (_unused) {
      setCsvHeaders([]);
    }
  };
  const handleFileChange = async e => {
    var _e$target$files;
    const file = (_e$target$files = e.target.files) === null || _e$target$files === void 0 ? void 0 : _e$target$files[0];
    if (file) {
      setCsvFile(file);
      setColumnMappings({});
      await parseHeaders(file);
    }
  };
  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = async e => {
    var _e$dataTransfer$files;
    e.preventDefault();
    setIsDragging(false);
    const file = (_e$dataTransfer$files = e.dataTransfer.files) === null || _e$dataTransfer$files === void 0 ? void 0 : _e$dataTransfer$files[0];
    if (file) {
      setCsvFile(file);
      setColumnMappings({});
      await parseHeaders(file);
    }
  };
  const handleRemoveFile = () => {
    setCsvFile(null);
    setCsvHeaders([]);
    setColumnMappings({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  if (dataRef) {
    dataRef.current = _objectSpread(_objectSpread({
      name: mappingName
    }, COLUMN_FIELD_MAP.reduce((acc, _ref7) => {
      let field = _ref7.field;
      return _objectSpread(_objectSpread({}, acc), {}, {
        [field]: columnMappings[field] || ''
      });
    }, {})), {}, {
      validate: () => {
        setShowErrors(true);
        return isValid;
      }
    });
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: 24
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Mapping name",
    placeholder: "Mapping name",
    value: mappingName,
    onChange: e => setMappingName(e.target.value),
    variant: "outlined",
    error: showErrors && !isNameValid,
    helperText: showErrors && !isNameValid ? 'Required' : undefined
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      border: "1px dashed ".concat(isDragging ? '#1976d2' : '#b8c4ce'),
      borderRadius: 4,
      padding: csvFile ? 12 : 48,
      cursor: !csvFile ? 'pointer' : 'default',
      marginBottom: 24,
      backgroundColor: isDragging ? 'rgba(25,118,210,0.04)' : undefined
    },
    onClick: () => {
      var _fileInputRef$current;
      return !csvFile && ((_fileInputRef$current = fileInputRef.current) === null || _fileInputRef$current === void 0 ? void 0 : _fileInputRef$current.click());
    },
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop
  }, !csvFile ? /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    align: "center",
    color: "light"
  }, "Upload a CSV to auto-detect column names (optional)") : /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      alignItems: 'center',
      gap: 12
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "file-text"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontWeight: 600
    }
  }, csvFile.name), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    size: "small",
    style: {
      minWidth: 0
    },
    onClick: e => {
      e.stopPropagation();
      handleRemoveFile();
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  }))), /*#__PURE__*/React.createElement("input", {
    ref: fileInputRef,
    type: "file",
    accept: ".csv",
    hidden: true,
    onChange: handleFileChange
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600,
      marginBottom: 4
    }
  }, "Map column names"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Map Livable field names to column names in your CSV"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER,
      paddingBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 12px 0 0',
      borderRight: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Livable field")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 0 0 12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "CSV column name"))), COLUMN_FIELD_MAP.map((_ref8, idx) => {
    let label = _ref8.label,
      field = _ref8.field,
      optional = _ref8.optional;
    return /*#__PURE__*/React.createElement("div", {
      key: field,
      style: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: idx < COLUMN_FIELD_MAP.length - 1 ? CELL_BORDER : undefined
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 12px 8px 0',
        borderRight: CELL_BORDER
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      style: {
        fontWeight: 600
      }
    }, label, optional && /*#__PURE__*/React.createElement(Typography, {
      component: "span",
      variant: "body2",
      color: "light",
      style: {
        fontWeight: 400,
        marginLeft: 4
      }
    }, "(optional)"))), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '4px 0 4px 12px'
      }
    }, csvHeaders.length > 0 ? /*#__PURE__*/React.createElement(FormControl, {
      size: "small",
      fullWidth: true,
      error: !!duplicateColumns[field] || showErrors && !optional && !columnMappings[field]
    }, /*#__PURE__*/React.createElement(Select, {
      value: columnMappings[field] || '',
      onChange: e => setColumnMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [field]: e.target.value
      })),
      displayEmpty: true,
      SelectDisplayProps: duplicateColumns[field] ? {
        'aria-invalid': true,
        'aria-describedby': "column-".concat(field, "-helper-text")
      } : undefined,
      renderValue: val => val ? val : /*#__PURE__*/React.createElement("span", {
        style: {
          color: '#9e9e9e'
        }
      }, "Select column")
    }, csvHeaders.filter(h => !Object.entries(columnMappings).some(_ref9 => {
      let _ref0 = _slicedToArray(_ref9, 2),
        f = _ref0[0],
        v = _ref0[1];
      return f !== field && v === h;
    })).map(h => /*#__PURE__*/React.createElement(MenuItem, {
      key: h,
      value: h
    }, h))), (duplicateColumns[field] || showErrors && !optional && !columnMappings[field]) && /*#__PURE__*/React.createElement(FormHelperText, {
      id: "column-".concat(field, "-helper-text")
    }, duplicateColumns[field] || 'Required')) : /*#__PURE__*/React.createElement(TextField, {
      size: "small",
      fullWidth: true,
      placeholder: "Column name",
      value: columnMappings[field] || '',
      onChange: e => setColumnMappings(prev => _objectSpread(_objectSpread({}, prev), {}, {
        [field]: e.target.value
      })),
      error: !!duplicateColumns[field] || showErrors && !optional && !columnMappings[field],
      helperText: duplicateColumns[field] || (showErrors && !optional && !columnMappings[field] ? 'Required' : undefined)
    })));
  }));
};
const MapColumnsViewContent = _ref1 => {
  var _mapping$unitMappings;
  let mapping = _ref1.mapping;
  const unitEntries = Array.isArray(mapping.unitMappings) ? mapping.unitMappings.map(_ref10 => {
    let unitId = _ref10.unitId,
      identifiers = _ref10.identifiers;
    return [unitId, (identifiers === null || identifiers === void 0 ? void 0 : identifiers[0]) || ''];
  }) : Object.entries((_mapping$unitMappings = mapping.unitMappings) !== null && _mapping$unitMappings !== void 0 ? _mapping$unitMappings : {});
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600,
      marginBottom: 4
    }
  }, "Map column names"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Livable field names mapped to your CSV column names"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER,
      paddingBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 12px 0 0',
      borderRight: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Livable field")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 0 0 12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "CSV column name"))), COLUMN_FIELD_MAP.map((_ref11, idx) => {
    var _mapping$columnMappin;
    let label = _ref11.label,
      field = _ref11.field;
    return /*#__PURE__*/React.createElement("div", {
      key: field,
      style: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: idx < COLUMN_FIELD_MAP.length - 1 ? CELL_BORDER : undefined
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 12px 8px 0',
        borderRight: CELL_BORDER
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      style: {
        fontWeight: 600
      }
    }, label)), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 0 8px 12px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, ((_mapping$columnMappin = mapping.columnMappings) === null || _mapping$columnMappin === void 0 ? void 0 : _mapping$columnMappin[field]) || mapping[field] || '—')));
  }), unitEntries.length > 0 && /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: 24
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    style: {
      fontWeight: 600,
      marginBottom: 4
    }
  }, "Map unit names"), /*#__PURE__*/React.createElement(Typography, {
    variant: "body1",
    color: "light",
    style: {
      marginBottom: 16
    }
  }, "Livable unit names mapped to your CSV unit names"), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      borderBottom: CELL_BORDER,
      paddingBottom: 8
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 12px 0 0',
      borderRight: CELL_BORDER
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "Livable unit name")), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1,
      padding: '0 0 0 12px'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    style: {
      fontWeight: 600
    }
  }, "CSV unit name"))), unitEntries.map((_ref12, idx) => {
    let _ref13 = _slicedToArray(_ref12, 2),
      livableName = _ref13[0],
      csvName = _ref13[1];
    return /*#__PURE__*/React.createElement("div", {
      key: livableName,
      style: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: idx < unitEntries.length - 1 ? CELL_BORDER : undefined
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 12px 8px 0',
        borderRight: CELL_BORDER
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      style: {
        fontWeight: 600
      }
    }, livableName)), /*#__PURE__*/React.createElement("div", {
      style: {
        flex: 1,
        padding: '8px 0 8px 12px'
      }
    }, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, csvName || '—')));
  })));
};
export const MapColumnsViewModal = _ref14 => {
  let mapping = _ref14.mapping;
  const _useState11 = useState(false),
    _useState12 = _slicedToArray(_useState11, 2),
    isOpen = _useState12[0],
    setIsOpen = _useState12[1];
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: /*#__PURE__*/React.createElement(Typography, {
      variant: "body1",
      style: {
        fontWeight: 600,
        cursor: 'pointer',
        textDecoration: 'underline'
      },
      onClick: () => setIsOpen(true)
    }, mapping.name || 'Mapping'),
    title: /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, mapping.name || 'Mapping'),
    rightButton: /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: () => setIsOpen(false)
    }, "Close"),
    isOpen: isOpen,
    handleOpen: () => setIsOpen(true),
    handleClose: () => setIsOpen(false)
  }, /*#__PURE__*/React.createElement(MapColumnsViewContent, {
    mapping: mapping
  }));
};
export const MapColumnsModal = _ref15 => {
  let onSave = _ref15.onSave,
    propertyId = _ref15.propertyId,
    utilityId = _ref15.utilityId,
    propertyUtilityId = _ref15.propertyUtilityId;
  const store = useStore();
  const _useState13 = useState(false),
    _useState14 = _slicedToArray(_useState13, 2),
    isOpen = _useState14[0],
    setIsOpen = _useState14[1];
  const _useState15 = useState(0),
    _useState16 = _slicedToArray(_useState15, 2),
    formKey = _useState16[0],
    setFormKey = _useState16[1];
  const dataRef = useRef({});
  const unitDataRef = useRef({});
  const validityStore = useLocalObservable(() => ({
    isFormValid: false,
    isUnitValid: true
  }));
  const handleFormValidChange = useCallback(valid => {
    validityStore.isFormValid = valid;
  }, [validityStore]);
  const handleUnitValidChange = useCallback(valid => {
    validityStore.isUnitValid = valid;
  }, [validityStore]);
  const handleOpen = () => {
    validityStore.isFormValid = false;
    validityStore.isUnitValid = true;
    setFormKey(k => k + 1);
    setIsOpen(true);
  };
  const handleClose = () => setIsOpen(false);
  const handleSave = async () => {
    var _dataRef$current$vali, _dataRef$current$vali2, _dataRef$current, _unitDataRef$current$, _unitDataRef$current$2, _unitDataRef$current, _store$homeDashboardS, _store$homeDashboardS2, _unitDataRef$current$3, _unitDataRef$current$4;
    const isColumnsFormValid = (_dataRef$current$vali = (_dataRef$current$vali2 = (_dataRef$current = dataRef.current).validate) === null || _dataRef$current$vali2 === void 0 ? void 0 : _dataRef$current$vali2.call(_dataRef$current)) !== null && _dataRef$current$vali !== void 0 ? _dataRef$current$vali : true;
    const isUnitSectionValid = (_unitDataRef$current$ = (_unitDataRef$current$2 = (_unitDataRef$current = unitDataRef.current).validate) === null || _unitDataRef$current$2 === void 0 ? void 0 : _unitDataRef$current$2.call(_unitDataRef$current)) !== null && _unitDataRef$current$ !== void 0 ? _unitDataRef$current$ : true;
    if (!isColumnsFormValid || !isUnitSectionValid) return;
    const data = dataRef.current;
    const units = (_store$homeDashboardS = (_store$homeDashboardS2 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.selectedPropertyDetails()) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.details) === null || _store$homeDashboardS2 === void 0 || (_store$homeDashboardS2 = _store$homeDashboardS2.unitSetup) === null || _store$homeDashboardS2 === void 0 ? void 0 : _store$homeDashboardS2.units) !== null && _store$homeDashboardS !== void 0 ? _store$homeDashboardS : [];
    const unitNameToId = Object.fromEntries(units.filter(u => u.id).map(u => [u.name, u.id]));
    const unitMappings = Object.entries((_unitDataRef$current$3 = unitDataRef.current.unitMappings) !== null && _unitDataRef$current$3 !== void 0 ? _unitDataRef$current$3 : {}).filter(_ref16 => {
      let _ref17 = _slicedToArray(_ref16, 2),
        csvName = _ref17[1];
      return csvName;
    }).map(_ref18 => {
      let _ref19 = _slicedToArray(_ref18, 2),
        unitName = _ref19[0],
        csvName = _ref19[1];
      return {
        unitId: unitNameToId[unitName] || unitName,
        identifiers: [csvName],
        isCommonArea: false
      };
    });
    const columnFields = COLUMN_FIELD_MAP.reduce((acc, _ref20) => {
      let field = _ref20.field;
      return _objectSpread(_objectSpread({}, acc), {}, {
        [field]: data[field] || ''
      });
    }, {});
    const resp = await store.allocations.postSubmeterBillingCsvMapAsync.run(_objectSpread(_objectSpread({
      propertyId,
      utilityId,
      propertyUtilityId,
      name: data.name
    }, columnFields), {}, {
      unitMappings
    }));
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
    toast('success', {
      body: ['Mapping saved!']
    });
    onSave === null || onSave === void 0 || onSave(_objectSpread(_objectSpread({
      id: resp.response,
      name: data.name,
      propertyUtilityId
    }, columnFields), {}, {
      columnMappings: columnFields,
      unitMappings: (_unitDataRef$current$4 = unitDataRef.current.unitMappings) !== null && _unitDataRef$current$4 !== void 0 ? _unitDataRef$current$4 : {}
    }));
    handleClose();
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: /*#__PURE__*/React.createElement(Button, {
      variant: "actionButtonInvert",
      size: "small",
      onClick: handleOpen
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "plus"
    }), /*#__PURE__*/React.createElement("span", {
      className: "actionText"
    }, "Add mapping")),
    title: /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Map columns"),
    subTitle: /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light"
    }, "Map your CSV meter read columns to our system's field names."),
    rightButton: /*#__PURE__*/React.createElement(CreateSaveButton, {
      validityStore: validityStore,
      onClick: handleSave
    }),
    isOpen: isOpen,
    handleOpen: handleOpen,
    handleClose: handleClose
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MapColumnsFormContent, {
    key: formKey,
    dataRef: dataRef,
    onValidChange: handleFormValidChange
  }), /*#__PURE__*/React.createElement(MapUnitNamesSection, {
    dataRef: unitDataRef,
    onValidChange: handleUnitValidChange
  })));
};
export const MapColumnsEditModal = _ref21 => {
  var _mapping$unitMappings2;
  let mapping = _ref21.mapping,
    onSave = _ref21.onSave,
    propertyId = _ref21.propertyId,
    utilityId = _ref21.utilityId,
    propertyUtilityId = _ref21.propertyUtilityId;
  const store = useStore();
  const _useState17 = useState(false),
    _useState18 = _slicedToArray(_useState17, 2),
    isOpen = _useState18[0],
    setIsOpen = _useState18[1];
  const _useState19 = useState(0),
    _useState20 = _slicedToArray(_useState19, 2),
    formKey = _useState20[0],
    setFormKey = _useState20[1];
  const dataRef = useRef({});
  const unitDataRef = useRef({});
  const validityStore = useLocalObservable(() => ({
    isFormValid: true,
    isUnitValid: true
  }));
  const handleFormValidChange = useCallback(valid => {
    validityStore.isFormValid = valid;
  }, [validityStore]);
  const handleUnitValidChange = useCallback(valid => {
    validityStore.isUnitValid = valid;
  }, [validityStore]);
  const handleOpen = () => {
    validityStore.isFormValid = true;
    validityStore.isUnitValid = true;
    setFormKey(k => k + 1);
    setIsOpen(true);
  };
  const handleClose = () => setIsOpen(false);
  const handleSave = async () => {
    var _dataRef$current$vali3, _dataRef$current$vali4, _dataRef$current2, _unitDataRef$current$5, _unitDataRef$current$6, _unitDataRef$current2, _store$homeDashboardS3, _store$homeDashboardS4, _unitDataRef$current$7, _unitDataRef$current$8;
    const isColumnsFormValid = (_dataRef$current$vali3 = (_dataRef$current$vali4 = (_dataRef$current2 = dataRef.current).validate) === null || _dataRef$current$vali4 === void 0 ? void 0 : _dataRef$current$vali4.call(_dataRef$current2)) !== null && _dataRef$current$vali3 !== void 0 ? _dataRef$current$vali3 : true;
    const isUnitSectionValid = (_unitDataRef$current$5 = (_unitDataRef$current$6 = (_unitDataRef$current2 = unitDataRef.current).validate) === null || _unitDataRef$current$6 === void 0 ? void 0 : _unitDataRef$current$6.call(_unitDataRef$current2)) !== null && _unitDataRef$current$5 !== void 0 ? _unitDataRef$current$5 : true;
    if (!isColumnsFormValid || !isUnitSectionValid) return;
    const data = dataRef.current;
    const units = (_store$homeDashboardS3 = (_store$homeDashboardS4 = store.homeDashboardStore.getCurrentCompany) === null || _store$homeDashboardS4 === void 0 || (_store$homeDashboardS4 = _store$homeDashboardS4.selectedPropertyDetails()) === null || _store$homeDashboardS4 === void 0 || (_store$homeDashboardS4 = _store$homeDashboardS4.details) === null || _store$homeDashboardS4 === void 0 || (_store$homeDashboardS4 = _store$homeDashboardS4.unitSetup) === null || _store$homeDashboardS4 === void 0 ? void 0 : _store$homeDashboardS4.units) !== null && _store$homeDashboardS3 !== void 0 ? _store$homeDashboardS3 : [];
    const unitNameToId = Object.fromEntries(units.filter(u => u.id).map(u => [u.name, u.id]));
    const unitMappings = Object.entries((_unitDataRef$current$7 = unitDataRef.current.unitMappings) !== null && _unitDataRef$current$7 !== void 0 ? _unitDataRef$current$7 : {}).filter(_ref22 => {
      let _ref23 = _slicedToArray(_ref22, 2),
        csvName = _ref23[1];
      return csvName;
    }).map(_ref24 => {
      let _ref25 = _slicedToArray(_ref24, 2),
        unitName = _ref25[0],
        csvName = _ref25[1];
      return {
        unitId: unitNameToId[unitName] || unitName,
        identifiers: [csvName],
        isCommonArea: false
      };
    });
    const columnFields = COLUMN_FIELD_MAP.reduce((acc, _ref26) => {
      let field = _ref26.field;
      return _objectSpread(_objectSpread({}, acc), {}, {
        [field]: data[field] || ''
      });
    }, {});
    const resp = await store.allocations.putSubmeterBillingCsvMapAsync.run(_objectSpread(_objectSpread({
      id: mapping.id,
      propertyId,
      utilityId,
      propertyUtilityId,
      name: data.name
    }, columnFields), {}, {
      unitMappings
    }));
    if (!(resp !== null && resp !== void 0 && resp.isSuccessful)) return;
    toast('success', {
      body: ['Mapping updated!']
    });
    onSave === null || onSave === void 0 || onSave(_objectSpread(_objectSpread(_objectSpread({}, mapping), {}, {
      name: data.name
    }, columnFields), {}, {
      columnMappings: columnFields,
      unitMappings: (_unitDataRef$current$8 = unitDataRef.current.unitMappings) !== null && _unitDataRef$current$8 !== void 0 ? _unitDataRef$current$8 : {}
    }));
    handleClose();
  };
  const initialUnitMappings = Array.isArray(mapping === null || mapping === void 0 ? void 0 : mapping.unitMappings) ? mapping.unitMappings.reduce((acc, _ref27) => {
    let unitId = _ref27.unitId,
      identifiers = _ref27.identifiers;
    return _objectSpread(_objectSpread({}, acc), {}, {
      [unitId]: (identifiers === null || identifiers === void 0 ? void 0 : identifiers[0]) || ''
    });
  }, {}) : (_mapping$unitMappings2 = mapping === null || mapping === void 0 ? void 0 : mapping.unitMappings) !== null && _mapping$unitMappings2 !== void 0 ? _mapping$unitMappings2 : {};
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: /*#__PURE__*/React.createElement(IconButton, {
      onClick: handleOpen
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "edit"
    })),
    title: /*#__PURE__*/React.createElement(Typography, {
      variant: "h4"
    }, "Edit mapping"),
    subTitle: /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "light"
    }, "Update your CSV meter read column mappings."),
    rightButton: /*#__PURE__*/React.createElement(EditSaveButton, {
      validityStore: validityStore,
      onClick: handleSave
    }),
    isOpen: isOpen,
    handleOpen: handleOpen,
    handleClose: handleClose
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MapColumnsFormContent, {
    key: formKey,
    dataRef: dataRef,
    onValidChange: handleFormValidChange,
    initialValues: mapping
  }), /*#__PURE__*/React.createElement(MapUnitNamesSection, {
    dataRef: unitDataRef,
    onValidChange: handleUnitValidChange,
    initialUnitMappings: initialUnitMappings
  })));
};